import * as React from "react";
import {notifyBugsnagAndShowToast} from "../../../../utils/Utils";
import {none, option, Option} from "ts-option";
import Job from "../../../../model/job/Job";
import {connect, Dispatch} from "react-redux";
import {OnFailure, OnSuccess, OnSuccessData} from "../../../../store/action/actionHelpers";
import {RootState} from "../../../../store/reducer/root/RootState";
import Loading from "../../../ui/loading/Loading";
import {api} from "../../../../api/api";
import Toast from "../../../helper/Toast";
import {BillOfMaterialsPreviewDetails} from "../../../../model/job/BillOfMaterialsPreviewDetails";
import JobBillOfMaterialsDetailsTableView from "./JobBillOfMaterialsDetailsTableView";
import QuoteDetails from "../../../../model/quote/QuoteDetails";
import JobBillOfMaterialsDetailsHeader from "./JobBillOfMaterialsDetailsHeader";
import JobBillOfMaterialsDetailsSummary from "./JobBillOfMaterialsDetailsSummary";
import EditAssetModal from "./EditAssetModal";
import AddAssetModal from "./AddAssetModal";
import RemoveAssetModal from "./RemoveAssetModal";
import AddMaterialModal from "./AddMaterialModal";
import RemoveMaterialModal from "./RemoveMaterialModal";
import {BillOfMaterialsPreviewItemOfMaterial} from "../../../../model/job/BillOfMaterialsPreviewItems";
import SuggestPriceModal from "./SuggestPriceModal";
import FinalisingBillOfMaterialsModal, {BillOfMaterialsFinaliseOptions} from "./FinalisingBillOfMaterialsModal";
import {
	fetchBillOfMaterialsByJobId,
	finaliseBillOfMaterials,
	updateBillOfMaterialsAsset
} from "../../../../store/action/billOfMaterials/billOfMaterials";
import SuggestedAssetPrice from "../../../../model/job/SuggestedAssetPrice";
import {fetchInvoice} from "../../../../store/action/invoice/invoice";
import {getInvoiceByJobId} from "../../../../store/view/invoice";
import {getBillByJobId} from "../../../../store/view/bill";
import {fetchBillByJobId} from "../../../../store/action/bill/bill";
import ConfirmShowFinalizeModal from "./ConfirmShowFinalizeModal";
import {getQuoteDetails} from "../../../../store/view/quote";
import {fetchQuoteDetails} from "../../../../store/action/quote/quote";
import {Bom} from "../../../../model/job/Bom";
import {BomItem} from "../../../../model/job/BomItems";
import {CreateUpdateBillOfMaterialsPreviewAssetApiDto} from "../../../../api/model/rest";
import {PriceStrategyPresenter} from "../../../quote/priceStrategyPicker/PriceStrategyPresenter";
import {getIndividualContact} from "../../../../store/view/contact";
import {fetchBrand} from "../../../../store/action/brand/brand";
import Brand from "../../../../model/brand/Brand";

interface ReduxProps {
	/**
	 * Details of quote
	 */
	quoteDetails: Option<QuoteDetails>;
	/**
	 * If true, invoice or bill related to job is in paid stage.
	 */
	readOnly: boolean,
	/**
	 * Brand identifier
	 */
	brandId: Option<string>
}

interface ComponentProps {
	/**
	 * Bill of materials details
	 */
	bom: Bom | BillOfMaterialsPreviewDetails
	/**
	 * Indicates if BoM is defined for job
	 */
	isBomDefinedForJob: boolean;
	/**
	 * Job
	 */
	job: Job
	/**
	 * Redirect to job
	 */
	redirectToJob: () => void
}

interface State {

	/**
	 * Details of quote fetched flag
	 */
	quoteDetailsFetched: boolean;
	/**
	 * Details of quote fetching flag
	 */
	quoteDetailsFetching: boolean;
	/**
	 * Indicates if add asset modal is visible
	 */
	addAssetModalVisible: boolean;
	/**
	 * Asset selected to remove
	 */
	assetToRemove: Option<BomItem>;
	/**
	 * Asset selected to edit
	 */
	assetToEdit: Option<BomItem>;
	/**
	 * Asset selected for material
	 */
	assetForMaterial: Option<BomItem>;
	/**
	 * Material selected to remove
	 */
	materialToRemove: Option<BillOfMaterialsPreviewItemOfMaterial>;
	/**
	 * Suggested asset sell price
	 */
	suggestedAssetSellPrice: Option<SuggestedAssetPrice>;
	/**
	 * Indicates if finalising BoM modal is visible
	 */
	finalizingBomModalVisible: boolean;
	/**
	 * Indicates if finalising modal confirmation is visible
	 */
	finalizingModalConfirmationVisible: boolean
	/**
	 * Is generate bill loading
	 */
	isGenerateBillLoading: boolean
}

interface DispatchProps {
	/**
	 * Finalize bill of materials
	 */
	finaliseBillOfMaterials: (bomId: string, selectedOptionData: Option<BillOfMaterialsFinaliseOptions>, jobId: string,
		onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Fetch invoice action.
	 * @param {string} jobId job identifier.
	 */
	fetchInvoice: (jobId: string) => void;
	/**
	 * Fetch bill action.
	 * @param {string} jobId job identifier.
	 */
	fetchBill: (jobId: string) => void;
	/**
	 * Fetch quote details action.
	 */
	fetchQuoteDetails: (quoteId: string, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Fetch bill of material
	 * @param jobId
	 * @param onSuccess
	 */
	fetchBillOfMaterialsByJobId: (jobId: string, onSuccess: OnSuccessData<Bom>, onFailure: OnFailure) => void
	/**
	 * Update bom item
	 */
	updateBillOfMaterialsAsset: (bomId: string, bomItemId: string, bomAsset: CreateUpdateBillOfMaterialsPreviewAssetApiDto,
		jobId: string, onSuccess: OnSuccess, onFailure: OnFailure) => void
	/**
	 * Fetch brand
	 * @param id
	 * @param onSuccess
	 * @param onFailure
	 */
	fetchBrand: (id: string, onSuccess: OnSuccessData<Brand>, onFailure: OnFailure) => void
}

type Props = ReduxProps & DispatchProps & ComponentProps

class BillOfMaterialDetails extends React.PureComponent<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			quoteDetailsFetched: false,
			quoteDetailsFetching: false,
			addAssetModalVisible: false,
			assetToRemove: none,
			assetToEdit: none,
			assetForMaterial: none,
			materialToRemove: none,
			suggestedAssetSellPrice: none,
			finalizingBomModalVisible: false,
			finalizingModalConfirmationVisible: false,
			isGenerateBillLoading: false
		};
	}

	componentDidMount(): void {
		this.props.fetchInvoice(this.props.job.id);
		this.props.fetchBill(this.props.job.id);
		this.checkQuoteExistsAndHasItems();

	}

	componentWillReceiveProps(nextProps: Props) {
		if (this.props.job.id !== nextProps.job.id) {
			this.props.fetchInvoice(nextProps.job.id);
			this.props.fetchBill(nextProps.job.id);
		}
		this.checkQuoteExistsAndHasItems(nextProps);
	}

	render() {
		const {bomDetails = this.props.bom} = {}
		const isBom = bomDetails instanceof Bom;
		return (
			<Loading isLoading={this.props.quoteDetails.isEmpty}>
				<JobBillOfMaterialsDetailsHeader job={this.props.job}
												 billOfMaterialsDetails={bomDetails}
												 onGenerateBillClick={this.showConfirmationModalOrGenerateBill}
												 readOnly={this.props.readOnly}
												 isBomDefinedForJob={this.props.isBomDefinedForJob}
												 isGenerateBillLoading={this.state.isGenerateBillLoading}/>
				<div className={'job-bill-of-materials_top-container'}>
					<JobBillOfMaterialsDetailsSummary billOfMaterialsDetails={bomDetails}
													  quoteDetails={this.props.quoteDetails}/>
					<PriceStrategyPresenter partsPriceStrategy={bomDetails.partsPriceStrategy}/>
				</div>

				<JobBillOfMaterialsDetailsTableView items={bomDetails.items}
													isBom={isBom}
													onShowAddAssetModalClick={() => this.setState(
														{addAssetModalVisible: true})}
													onRemoveAssetClick={(asset) => this.setState(
														{assetToRemove: option(asset)})}
													onEditAssetClick={(asset) => this.setState(
														{assetToEdit: option(asset)})}
													onShowAddMaterialModalClick={(asset) => this.setState(
														{assetForMaterial: option(asset)})}
													onRemoveMaterialClick={(asset, material) => this.setState(
														{
															assetForMaterial: option(asset),
															materialToRemove: option(material)
														})}
													editable={!this.props.readOnly}
													markAsHiberParts={this.markAsHiberParts}/>
				{isBom && this.state.addAssetModalVisible &&
				<AddAssetModal bomId={(bomDetails as Bom).id}
							   jobId={this.props.job.id}
							   closeModal={this.closeAddAssetModal}
							   partsPriceStrategy={bomDetails.partsPriceStrategy}/>
				}
				{isBom && this.state.assetToRemove.isDefined &&
				<RemoveAssetModal bomId={(bomDetails as Bom).id}
								  jobId={this.props.job.id}
								  selectedAsset={this.state.assetToRemove.get}
								  closeModal={this.closeRemoveAssetModal}/>
				}
				{isBom && this.state.assetToEdit.isDefined &&
				<EditAssetModal bomAsset={this.state.assetToEdit.get}
								bomDetailsId={(bomDetails as Bom).id}
								jobId={this.props.job.id}
								closeModal={this.closeEditAssetModal}/>
				}
				{isBom && this.state.assetForMaterial.isDefined &&
				this.state.materialToRemove.isEmpty && this.state.suggestedAssetSellPrice.isEmpty &&
				<AddMaterialModal bomDetailsId={(bomDetails as Bom).id}
								  bomMerchantId={(bomDetails as Bom).merchantId}
								  jobId={this.props.job.id}
								  selectedAsset={this.state.assetForMaterial.get}
								  onSuccess={this.onMaterialAdded}
								  onCancel={this.closeAddMaterialModal}/>
				}
				{isBom && this.state.assetForMaterial.isDefined &&
				this.state.materialToRemove.isDefined &&
				<RemoveMaterialModal bomDetailsId={(bomDetails as Bom).id}
									 selectedAsset={this.state.assetForMaterial.get}
									 selectedMaterial={this.state.materialToRemove.get}
									 jobId={this.props.job.id}
									 onSuccess={this.onMaterialRemoved}
									 onCancel={this.closeRemoveMaterialModal}/>
				}
				{isBom && this.state.suggestedAssetSellPrice.isDefined &&
				this.state.assetForMaterial.isDefined &&
				<SuggestPriceModal bomDetailsId={(bomDetails as Bom).id}
								   suggestedAssetSellPrice={this.state.suggestedAssetSellPrice.get}
								   selectedAsset={this.state.assetForMaterial.get}
								   jobId={this.props.job.id}
								   closeModal={this.closeSuggestPriceModal}/>
				}
				{isBom && this.state.finalizingBomModalVisible &&
				this.areBomCostsDefined(bomDetails as Bom) &&
				<FinalisingBillOfMaterialsModal bomId={(bomDetails as Bom).id}
												customerPrice={bomDetails.customerPrice}
												installerEarnings={(bomDetails as Bom).installerEarnings.get}
												costsDifference={(bomDetails as Bom).customerPriceDifference.get}
												jobId={this.props.job.id}
												onSuccess={this.closeFinalisingBomModal}
												onCancel={this.closeFinalisingBomModal}/>}
				{this.state.finalizingModalConfirmationVisible &&
				<ConfirmShowFinalizeModal
					onClose={() => this.setState({finalizingModalConfirmationVisible: false})}
					onConfirm={this.generateBill}/>}
			</Loading>
		);
	}

	checkQuoteExistsAndHasItems(props: Props = this.props) {
		if (props.quoteDetails.isEmpty) {
			if (this.state.quoteDetailsFetched) {
				return
			}
			else if (!this.state.quoteDetailsFetching) {
				props.job.quoteId.map(qId => {
					this.setState({quoteDetailsFetching: true});
					props.fetchQuoteDetails(qId, () => this.setState({quoteDetailsFetched: true, quoteDetailsFetching: false}),
						(error) => {
							notifyBugsnagAndShowToast(error, "Cannot fetch quote details", "Bill of materials");
							this.props.redirectToJob();
						})
				})

			}
		}
		else {
			if (props.quoteDetails.get.hasNoAssets()) {
				Toast.error("Quote should include assets", "Bill of materials");
				this.props.redirectToJob();
			}
		}
	}

	onAssetAdded = () => {
		this.closeAddAssetModal();
	};

	closeAddAssetModal = () => {
		this.setState({addAssetModalVisible: false});
		api.instance.assetApi.clearAssetsCache();
	};

	closeRemoveAssetModal = () => {
		this.setState({assetToRemove: none});
	};

	closeEditAssetModal = () => {
		this.setState({assetToEdit: none});
	};

	onMaterialAdded = (price: SuggestedAssetPrice) => {
		api.instance.merchantApi.clearMaterialsCache();
		this.props.readOnly ? this.setState({assetForMaterial: none}) : this.setState({suggestedAssetSellPrice: option(price)});
	};

	closeAddMaterialModal = () => {
		this.setState({assetForMaterial: none});
		api.instance.merchantApi.clearMaterialsCache();
	};

	onMaterialRemoved = (price: SuggestedAssetPrice) => {
		this.setState({
			materialToRemove: none, ...this.props.readOnly ?
				{suggestedAssetSellPrice: none, assetForMaterial: none} :
				{suggestedAssetSellPrice: option(price), assetForMaterial: this.state.assetForMaterial}
		});
	};

	closeRemoveMaterialModal = () => {
		this.setState({assetForMaterial: none, materialToRemove: none});
	};

	closeSuggestPriceModal = () => {
		this.setState({assetForMaterial: none, suggestedAssetSellPrice: none})
	};

	areBomCostsDefined(bomDetails: Bom) {
		return bomDetails.installerEarnings.isDefined && bomDetails.customerPriceDifference.isDefined;
	}

	generateBill = () => {
		if (this.props.bom instanceof Bom) {
			if (this.props.bom.customerPriceDifference.map(diff => diff.amount == 0).getOrElse(false)) {
				this.setState({finalizingModalConfirmationVisible: false})
				this.props.finaliseBillOfMaterials((this.props.bom as Bom).id, none, this.props.job.id,
					() => Toast.success("Bill has been generated.", "Bill of Materials"),
					(error) => notifyBugsnagAndShowToast(error, 'Finalise Bill of Materials')
				)
			}
			else {
				this.setState({finalizingBomModalVisible: true, finalizingModalConfirmationVisible: false});
			}
		}
	};

	closeFinalisingBomModal = () => {
		this.setState({finalizingBomModalVisible: false});
	};

	showConfirmationModalOrGenerateBill = () => {
		this.setState({isGenerateBillLoading: true}, () =>
			this.props.fetchBillOfMaterialsByJobId(this.props.job.id, (bom) => {
				this.setState({isGenerateBillLoading: false});
				if (bom.profit.parts.map(p => Math.abs(p.amount) > 100).getOrElse(false)) {
					this.setState({finalizingModalConfirmationVisible: true})
				}
				else {
					this.generateBill();
				}
			}, (e) => {
				this.setState({isGenerateBillLoading: false});
				notifyBugsnagAndShowToast(e, "Job bill of materials details",
					"Cannot fetch job bill of materials details")
			})
		)
	}

	markAsHiberParts = (bomItem: BomItem) => {
		if (this.props.bom instanceof Bom) {
			const {bom} = this.props
			this.props.brandId.map(
				id => this.props.fetchBrand(id, brand => {
					this.props.updateBillOfMaterialsAsset(bom.id, bomItem.id,
						bomItem.copy({selfSourced: false, markup: brand.customPartsMarkup}).toJson(),
						this.props.job.id,
						() => Toast.success("Asset marked as hiber successfully", "Mark as hiber part"),
						(e) => notifyBugsnagAndShowToast(e, "Mark as hiber part"))
				}, e => notifyBugsnagAndShowToast(e, "Fetch brand"))
			)
		}
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		finaliseBillOfMaterials: (bomId: string, selectedOptionData: Option<BillOfMaterialsFinaliseOptions>, jobId: string,
			onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			finaliseBillOfMaterials(bomId, selectedOptionData, jobId, onSuccess, onFailure)),
		fetchInvoice: (jobId: string) => dispatch(fetchInvoice(jobId, () => {}, () => {})),
		fetchBill: (jobId: string) => dispatch(fetchBillByJobId(jobId, () => {}, () => {})),
		fetchQuoteDetails: (quoteId: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			fetchQuoteDetails(quoteId, onSuccess, onFailure)),
		fetchBillOfMaterialsByJobId: (jobId: string, onSuccess: OnSuccessData<Bom>, onFailure: OnFailure) => {
			dispatch(
				fetchBillOfMaterialsByJobId(jobId, onSuccess, onFailure))
		},
		updateBillOfMaterialsAsset: (bomId: string, assetId: string, bomAsset: CreateUpdateBillOfMaterialsPreviewAssetApiDto,
			jobId: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			updateBillOfMaterialsAsset(bomId, assetId, bomAsset, jobId, onSuccess, onFailure)
		),
		fetchBrand: (id: string, onSuccess: OnSuccessData<Brand>, onFailure: OnFailure) => dispatch(
			fetchBrand(id, onSuccess, onFailure))
	}
}

function mapStateToProps(state: RootState, props: Props) {
	const jobId = props.job.id
	const quoteDetails = props.job.quoteId.flatMap(qId => getQuoteDetails(qId, state));
	const isInvoicePaid = getInvoiceByJobId(jobId, state).map(inv => inv.isPaid()).getOrElse(false);
	const isBillPaid = getBillByJobId(jobId, state).map(bill => bill.isPaid()).getOrElse(false);
	const readOnly = isInvoicePaid || isBillPaid;
	const brandId = getIndividualContact(props.job.data.principalContactId, state).map(c => c.data.brandId)
	return {quoteDetails, readOnly, brandId};
}

export default connect<ReduxProps, DispatchProps>(mapStateToProps, mapDispatchToProps)(BillOfMaterialDetails)
